import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PICKUP_SLOTS } from "@/constants/wizard";
import { api, formatApiError, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { IndianRupee } from "lucide-react";

export default function OrderBookingDrawer({ open, onClose, payload }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    address_line1: "",
    address_line2: "",
    city: "Mumbai",
    pincode: "",
    pickup_date: "",
    pickup_slot: "morning",
    notes: "",
    referrer_code: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [referrerName, setReferrerName] = useState("");
  const [referrerChecking, setReferrerChecking] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const verifyReferral = async () => {
    const code = form.referrer_code.trim().toUpperCase();
    if (!code) return;
    setReferrerChecking(true);
    try {
      const { data } = await api.get(`/referrals/lookup?code=${encodeURIComponent(code)}`);
      setReferrerName(data.referrer_name);
      toast.success(`Referral verified — from ${data.referrer_name}`);
    } catch (err) {
      setReferrerName("");
      toast.error("Invalid referral code");
    } finally {
      setReferrerChecking(false);
    }
  };

  if (!payload) return null;
  const { product, storage, answers, quote } = payload;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_phone || !form.address_line1 || !form.pincode || !form.pickup_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        product_id: product.id,
        product_name: product.name,
        storage,
        conditions: answers,
        final_price: quote.final_price,
        ...form,
      };
      const { data } = await api.post("/orders", body);
      toast.success(`Pickup booked! Reference ${data.ref}`);
      onClose();
      navigate(`/order/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split("T")[0];

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="booking-drawer">
        <SheetHeader className="mb-4">
          <SheetTitle>Book Free Doorstep Pickup</SheetTitle>
        </SheetHeader>

        <div className="rounded-xl border border-border p-4 bg-secondary/40 mb-6">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Confirmed offer</div>
          <div className="font-semibold mt-1">{product.name} · {storage}</div>
          <div className="mt-2 flex items-end gap-1 text-accent">
            <IndianRupee className="w-5 h-5" />
            <span className="text-2xl font-extrabold font-mono">{quote.final_price.toLocaleString("en-IN")}</span>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
          <div className="grid gap-2">
            <Label htmlFor="cn">Full Name *</Label>
            <Input id="cn" data-testid="input-name" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cp">WhatsApp Number *</Label>
            <Input id="cp" data-testid="input-phone" value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} placeholder="10-digit number" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="a1">Flat / Building *</Label>
            <Input id="a1" data-testid="input-addr1" value={form.address_line1} onChange={(e) => update("address_line1", e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="a2">Street / Landmark</Label>
            <Input id="a2" data-testid="input-addr2" value={form.address_line2} onChange={(e) => update("address_line2", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="city">City *</Label>
              <Input id="city" data-testid="input-city" value={form.city} onChange={(e) => update("city", e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="pin">Pincode *</Label>
              <Input id="pin" data-testid="input-pincode" value={form.pincode} onChange={(e) => update("pincode", e.target.value)} maxLength={6} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="pd">Pickup Date *</Label>
              <Input id="pd" type="date" min={minDateStr} data-testid="input-pickup-date" value={form.pickup_date} onChange={(e) => update("pickup_date", e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Time Slot *</Label>
              <Select value={form.pickup_slot} onValueChange={(v) => update("pickup_slot", v)}>
                <SelectTrigger data-testid="select-pickup-slot"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PICKUP_SLOTS.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`slot-${s.value}`}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="nt">Notes (optional)</Label>
            <Textarea id="nt" data-testid="input-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Any specific pickup instruction…" />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="rc">Referral Code (optional)</Label>
            <div className="flex gap-2">
              <Input
                id="rc"
                data-testid="input-referrer-code"
                value={form.referrer_code}
                onChange={(e) => {
                  update("referrer_code", e.target.value.toUpperCase());
                  setReferrerName("");
                }}
                placeholder="TV-XXXXX"
                className="uppercase font-mono"
              />
              <Button
                type="button"
                variant="outline"
                onClick={verifyReferral}
                disabled={referrerChecking || !form.referrer_code}
                data-testid="verify-referrer-btn"
              >
                {referrerChecking ? "…" : "Verify"}
              </Button>
            </div>
            {referrerName && (
              <div className="text-xs text-accent font-medium">
                ✓ Referred by {referrerName} — they&apos;ll earn ₹200 when your pickup completes
              </div>
            )}
          </div>

          <div className="rounded-xl border border-accent/30 bg-accent/5 p-3 text-xs text-accent-foreground/80 font-medium">
            💵 <span className="text-foreground">100% Cash / UPI paid on-doorstep after 3-minute inspection. Zero online advance.</span>
          </div>

          <Button type="submit" disabled={submitting} className="w-full h-11 bg-accent hover:bg-accent/90" data-testid="submit-booking-btn">
            {submitting ? "Booking…" : `Confirm Pickup · ${formatINR(quote.final_price)}`}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
